import { Box, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { RequireNonAggregationRow } from "components/strategyDashboard/heats/RequireNonAggregationRow";
import { TFunction } from "i18next";
import {
  ConsumptionSourceName,
  HeatSeries,
  MaterialRead,
  StrikeRule,
} from "src/store/api/generatedApi";
import {
  RatioRecipeDisplay,
  calculateRecipeValues,
} from "./cells/RatioRecipeDisplay";
import { RecipeHeader } from "./cells/RecipeHeader";

/**
 * Displays recipes as ratios (e.g., "2:1:2 +3") or percentages (e.g., "46% 15% 31% 8%").
 * Actual Recipe column colors are based on diff from Planned (green = more, red = less).
 */
export const createRatioRecipeColumns = (
  t: TFunction,
  availableMixSources: ConsumptionSourceName[],
  materials: MaterialRead[],
  showTotalLifts: boolean,
  ignoredMaterials: Set<string>,
  strikeRules: Map<string, StrikeRule>
): GridColDef<HeatSeries>[] => {
  const materialNames = materials.map((m) => m.name);
  const materialShortNames = Object.fromEntries(
    materials.map((m) => [m.name, m.short_name])
  );

  const createRecipeColumn = (
    source: ConsumptionSourceName,
    size: number
  ): GridColDef<HeatSeries> => ({
    field: `ratio_${source}`,
    headerName: t(source),
    width: size === 1 ? 200 : 160,
    sortable: false,
    resizable: false,
    headerAlign: "center",
    renderHeader: () => (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          width: "100%",
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: "fontWeightSemiBold", textAlign: "center" }}
        >
          {t("sourceLifts", { source: t(source) })}
        </Typography>
        <RecipeHeader
          materials={materialNames}
          shortNames={materialShortNames}
          ignoredMaterials={ignoredMaterials}
          size="0.65rem"
        />
      </Box>
    ),
    renderCell: RequireNonAggregationRow(({ row }) => {
      const recipeValues = calculateRecipeValues(
        row,
        source,
        materialNames,
        showTotalLifts,
        ignoredMaterials,
        strikeRules
      );
      if (recipeValues.length === 0) {
        return (
          <Typography variant="body2" color="text.secondary">
            {t("noSourceData", { source: t(source) })}
          </Typography>
        );
      }

      const plannedRecipeValues =
        source !== "plannedSource" &&
        availableMixSources.includes("plannedSource")
          ? calculateRecipeValues(
              row,
              "plannedSource",
              materialNames,
              showTotalLifts,
              ignoredMaterials,
              strikeRules
            )
          : undefined;

      return (
        <RatioRecipeDisplay
          values={recipeValues}
          plannedValues={plannedRecipeValues}
        />
      );
    }),
  });

  return availableMixSources.map((source) =>
    createRecipeColumn(source, availableMixSources.length)
  );
};
